<template>
  <div>
    <FormHeader :formStep="formStep" @onBackClick="onBack" />
    <FormContainer>
      <TransitionWrapper>
        <FormTitle>{{ t('renovationYear.notificationTitle') }}</FormTitle>
        <FormContent>
          <div :class="$style.wrapper">
            <FormActions space="0px">
              <HgInput
                ref="inputRenovationYear"
                v-model.trim="v$.renovationYear.$model as any"
                name="renovationYear"
                :label="t('renovationYear.inputLabel')"
                :maxlength="4"
                inputmode="numeric"
                pattern="[0-9]*"
                :isInvalid="v$.renovationYear.$invalid"
                :isDirty="isRenovationYearDirty"
                @input="onBlur"
              >
                <template #errorMessages>
                  <span v-if="v$.renovationYear.required.$invalid">
                    {{ t('valuationForm.propertyDetails.yearBuilt.required') }}
                  </span>
                  <span
                    v-if="!v$.renovationYear.len.$invalid && v$.renovationYear.min.$invalid"
                    ref="minValueError"
                  >
                    {{ t('renovationYear.error.invalidValue') }}
                  </span>
                  <span
                    v-if="!v$.renovationYear.len.$invalid && v$.renovationYear.max.$invalid"
                    ref="maxValueError"
                  >
                    {{ t('renovationYear.error.presentValue') }}
                  </span>
                </template>
              </HgInput>
            </FormActions>
            <FormBottom :onNext="onNext" />
          </div>
        </FormContent>
      </TransitionWrapper>
    </FormContainer>
  </div>
</template>

<script lang="ts" setup>
import { useVuelidate } from '@vuelidate/core';
import { maxValue, minLength, minValue, required } from '@vuelidate/validators';
import { storeToRefs } from 'pinia';
import { computed, nextTick, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute } from 'vue-router';

import {
  FormActions,
  FormBottom,
  FormContainer,
  FormContent,
  FormHeader,
  FormTitle,
  TransitionWrapper,
} from '@/components/common';
import { useTrackingWithTreatment } from '@/composables/useTrackingWithTreatment';
import { inputStringToNumber } from '@/helpers/Number';
import { usePropertyValuationStore } from '@/stores/propertyValuation';
import { getStepInputs, isPropertyDetailDataPrefilled } from '@/tracking/tracking';
import { MAX_RENOVATION_YEAR, ValuationFlow } from '@/types';
import { HgInput } from '@smg-real-estate/domus';

const propertyValuationStore = usePropertyValuationStore();
const {
  data: formData,
  step: formStep,
  propertyDetailsData,
  selectedPropertyDetailIndex,
} = storeToRefs(propertyValuationStore);
const { t, locale } = useI18n();
const route = useRoute();
const { trackVirtualPageViewWithTreatment } = useTrackingWithTreatment();

// Reactive data
const renovationYear = ref<string>('');
const isDirty = ref(false);
const inputRenovationYear = ref();

const isRenovationYearDirty = computed((): boolean => {
  return !!renovationYear.value || isDirty.value;
});

// Validation rules
const rules = computed(() => ({
  renovationYear: {
    required,
    min: minValue(formData.value.buildYear ?? 0),
    max: maxValue(MAX_RENOVATION_YEAR),
    len: minLength(4),
  },
}));

// Vuelidate setup
const v$ = useVuelidate(rules, { renovationYear });

// Methods
const focusInput = async (): Promise<void> => {
  await nextTick();
  const inputElement = inputRenovationYear.value?.$el?.querySelector(
    'input[name="renovationYear"]',
  );
  if (inputElement) {
    inputElement.focus();
  }
};

const onNext = (): void => {
  v$.value.$touch();
  if (v$.value.$invalid) {
    isDirty.value = true;
    return;
  }
  propertyValuationStore.setFormData({
    renovationYear: inputStringToNumber(renovationYear.value || ''),
  });
  propertyValuationStore.navigateNext(ValuationFlow.RenovationYear);
};

const onBlur = (value: any): void => {
  renovationYear.value = value.target.value;
};

const onBack = (): void => {
  propertyValuationStore.navigateBack(ValuationFlow.RenovationYear);
};

const initializeTouchEvent = (): void => {
  if (renovationYear.value) {
    v$.value.$touch();
  }
};

// Lifecycle
onMounted(() => {
  renovationYear.value = formData.value.renovationYear?.toString() || '';

  trackVirtualPageViewWithTreatment({
    subType: 'input_renovationYear',
    locale: locale.value,
    stepInputs: getStepInputs(
      {
        ...formData.value,
        aptDetails: isPropertyDetailDataPrefilled(
          propertyDetailsData.value,
          selectedPropertyDetailIndex.value,
        ),
      },
      route.query.source?.toString() || '',
      formStep.value,
    ),
  });

  focusInput();
  initializeTouchEvent();
});
</script>

<style lang="scss" module>
@use '@/assets/variables' as *;

.wrapper {
  position: relative;
}
</style>
